import React from "react";
import { motion } from "framer-motion";
import { CONTACT } from "@/constants/testIds";
import { useSite } from "@/lib/site";

export default function Contact() {
    const site = useSite();

    return (
        <section
            id="contact"
            data-testid={CONTACT.section}
            className="py-28 md:py-44 border-t border-[#E6E2D6]"
        >
            <div className="mx-auto max-w-[1800px] px-6 md:px-12 lg:px-20">
                {/* Section header */}
                <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16 md:mb-24">
                    <div className="col-span-12 md:col-span-3">
                        <p className="eyebrow text-[#1B1B1B]/70">
                            § Contact
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-9">
                        <motion.h2
                            data-testid={CONTACT.heading}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{
                                duration: 1,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="font-serif-editorial text-5xl md:text-7xl lg:text-[110px] leading-[0.98] tracking-[-0.02em] text-[#1B1B1B] max-w-5xl"
                        >
                            Let's build something{" "}
                            <em className="italic text-[#A8844F]">
                                meaningful.
                            </em>
                        </motion.h2>
                    </div>
                </div>

                {/* Focal-point email — the visual anchor of the page */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                    className="border-y border-[#E6E2D6] py-16 md:py-24 lg:py-28"
                >
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-10">
                        <p className="eyebrow text-[#1B1B1B]/60">
                            Correspondence — write to us
                        </p>
                        <p className="eyebrow text-[#1B1B1B]/60">
                            Reply within two business days
                        </p>
                    </div>
                    <a
                        data-testid={CONTACT.email}
                        href={`mailto:${site.contact_email}`}
                        className="group block font-serif-editorial text-[#1B1B1B] tracking-[-0.03em] leading-[1.02] whitespace-normal break-words transition-colors duration-500 hover:text-[#A8844F]"
                        style={{ fontSize: "clamp(1.75rem, 8.5vw, 11rem)" }}
                    >
                        {(() => {
                            const [local, domain = ""] = (site.contact_email || "").split("@");
                            const [name, tld] = (domain || "").split(/\.(?=[^.]+$)/);
                            return (
                                <>
                                    {local}
                                    <wbr />
                                    <span className="text-[#A8844F]">@</span>
                                    {name}
                                    <span className="text-[#A8844F]">.</span>
                                    {tld}
                                </>
                            );
                        })()}
                    </a>
                </motion.div>

                {/* Notes — no form; a short word on what to write about */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14 mt-16 md:mt-20">
                    <div>
                        <p className="eyebrow text-[#1B1B1B]/60 mb-3">
                            New Business
                        </p>
                        <p className="font-sans-tight text-base leading-[1.65] text-[#1B1B1B]/80 max-w-xs font-light">
                            Selective engagements, worldwide. Share the
                            ambition and the timeline — we reply personally.
                        </p>
                    </div>
                    <div>
                        <p className="eyebrow text-[#1B1B1B]/60 mb-3">
                            Press · Speaking
                        </p>
                        <p className="font-sans-tight text-base leading-[1.65] text-[#1B1B1B]/80 max-w-xs font-light">
                            For editorial features, lectures or interviews,
                            please write to the same address.
                        </p>
                    </div>
                    <div>
                        <p className="eyebrow text-[#1B1B1B]/60 mb-3">
                            General
                        </p>
                        <p className="font-sans-tight text-base leading-[1.65] text-[#1B1B1B]/80 max-w-xs font-light">
                            One address, no forms. Everything reaches the
                            studio directly.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
